import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import UnsavedChangesModal from 'components/Modals/UnsavedChangesModal/UnsavedChangesModal'
import useApi from 'hooks/useApi'
import { WppBreadcrumb, WppTypography } from '@wppopen/components-library-react'
import { GenericModal, ManualUpload } from 'components/Modals'
import appContext from 'context/appContext'
import ProjectPageContext from 'context/projectContext/projectContext'
import { ProductContext } from 'context/productContext/productContext'
import Tabs from 'components/Tabs/Tabs'
import { MenuItems } from 'components/MenuListPopUp/MenuListPopUp'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import PageHeader from 'components/PageHeader/PageHeader'
import Banner from 'components/Banner'
import { createCsApiUrlFromEndpoint, getFormattedDate } from 'utils'
import { getActiveRegionRetailers } from '../utils'
import { hasQualityIncomplete, renderStatus } from 'components/RenderSelectedProjectRegion/utils'
import clsx from 'clsx'
import useToast from 'hooks/useToast'
import RenderNotGeneratedProduct from 'components/RenderNotGeneratedProduct/RenderNotGeneratedProduct'
import RenderGeneratedProduct from 'components/RenderGeneratedProduct/RenderGeneratedProduct'
import { statusChecker } from 'components/StatusMessage/Status'
import usePermissions from 'hooks/usePermissions'
import useInitialiser from 'hooks/useInitialiser'
import { unknown } from 'utils/constants'
import type { productTabsIds } from 'components/Tabs/tabs-type'
import type { FEApiError } from 'utils/errorHandling'
import type { ExportListData } from '@contracts'
import type { WppBreadcrumbCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { BreadcrumbItemEventDetails } from '@wppopen/components-library'
import type { ProductsDetailDataV5 } from 'utils/utils.d'
import styles from '../ProjectView.module.scss'
import navigationHeaderStyles from '../../Settings/NavigationHeader.module.scss'

export default function SingleProductView() {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useContext(appContext)
  const { productAPI, currentTab, setCurrentTab } = useContext(ProductContext)
  const {
    singleProjectApiDetails,
    setToastDetailsContext,
    setActiveRegion,
    activeRegionData,
    permissions: projectPermissions,
    clientDetails,
  } = useContext(ProjectPageContext)
  const [isTryingToQuitWithChanges, setIsTryingToQuitWithChanges] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string>()
  const [isDeleteActive, setIsDeleteActive] = useState<boolean>(false)
  const navigate = useNavigate()
  const [isProductDataFormActive, setIsProductDataFormActive] = useState(false)
  const [showIconInTab, setShowIconInTab] = useState<boolean[]>(new Array(3).fill(false))
  const { product_id } = useParams()
  const { toastDetails, setToastDetails } = useToast()
  const [isBulkEditContentModalOpen, setIsBulkEditContentModalOpen] = useState(false)
  const { configApi } = useContext(appContext)
  const { data: configData } = configApi
  const retailersData = useMemo(
    () => getActiveRegionRetailers(configData, activeRegionData),
    [configData?.retailers, activeRegionData?.retailers],
  )

  // get project details
  const { data: singleProjectData } = singleProjectApiDetails
  const { project_name } = singleProjectData || {}

  // fetch single product details
  const { data: getSingleProductData, isLoading: isGetSingleProductLoading, error: getSingleProductError } = productAPI
  const { isInitialising } = useInitialiser({
    subscribedApis: projectPermissions.canGetProject ? [productAPI] : [singleProjectApiDetails],
  })

  const {
    fetchData: deleteProduct,
    url: deleteProductUrl,
    isLoading: isDeleteProductLoading,
  } = useApi<ProductsDetailDataV5>(createCsApiUrlFromEndpoint(`products/${product_id}`))

  const clientId = clientDetails?.id

  const { clientLevelPermissions } = usePermissions({
    clientId,
    permissionSubset: ['PDP_APP_GET_PRODUCTS_BY', 'PDP_APP_DELETE_PRODUCTS_BY', 'PDP_APP_GET_PRODUCTS_EXPORT'],
  })

  const [canGetProduct, canDeleteProduct, canExportProduct] = clientLevelPermissions

  const { fetchData: exportProductDataFetch } = useApi<ExportListData>('products/export')

  const breadCrumbItems = useMemo(
    () => [
      {
        label: 'Projects',
        path: '/',
      },
      {
        label: project_name || 'Project',
        path: `/project/${getSingleProductData?.project_id}`,
      },
      {
        label: getSingleProductData?.reference_name || 'Product',
        path: `product/${product_id}`,
      },
    ],
    [project_name, getSingleProductData?.reference_name, getSingleProductData?.project_id],
  )

  // update region id in context
  useEffect(() => {
    setActiveRegion(getSingleProductData?.project_region_id)
  }, [getSingleProductData?.project_region_id])

  // every time currentTab changes form should be closed
  useEffect(() => {
    setIsProductDataFormActive(false)
  }, [currentTab])

  // handle breadcrumb click
  const handleRouteChange = (e: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => {
    if (isInitialising) return
    const { path } = e.detail

    if (hasUnsavedChanges) {
      setPendingNavigation(path)
      setIsTryingToQuitWithChanges(true)
      return
    }

    navigate(path)
  }

  const handleDeleteProduct = async () => {
    try {
      await deleteProduct(deleteProductUrl, {
        method: 'DELETE',
      })

      setToastDetailsContext({
        message: 'Product deleted successfully',
        type: 'success',
      })
      navigate(breadCrumbItems[1].path)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  useEffect(() => {
    if (getSingleProductData?.variants) {
      setShowIconInTab(prev => {
        const newShowIconIn = [...prev]
        newShowIconIn[1] = hasQualityIncomplete(getSingleProductData.variants)
        return newShowIconIn
      })
    }
  }, [getSingleProductData?.variants])

  const handleDownloadProduct = async () => {
    if (!getSingleProductData) return
    try {
      const response = await exportProductDataFetch(
        `products/export?project_region_id=${getSingleProductData.project_region_id}&product_ids=${getSingleProductData.product_id}`,
      )
      const { download_url } = response

      if (!download_url) return
      const a = document.createElement('a')
      a.href = download_url
      a.download = `products-${getSingleProductData.reference_name}`
      a.click()
      a.remove()
    } catch (err) {
      console.error(err)
    }
  }

  const menuItems: MenuItems = useMemo(() => {
    const returnMenuItems: MenuItems = []

    if (canExportProduct) returnMenuItems.push({ label: 'Download product', onClick: () => handleDownloadProduct() })
    if (canDeleteProduct) returnMenuItems.push({ label: 'Delete product', onClick: () => setIsDeleteActive(true) })
    returnMenuItems.push({ label: 'Upload content', onClick: () => setIsBulkEditContentModalOpen(true) })

    return returnMenuItems
  }, [canDeleteProduct, canExportProduct, handleDownloadProduct])

  if (
    !isInitialising &&
    (!projectPermissions.canGetProject ||
      !canGetProduct ||
      getSingleProductError?.status === 404 ||
      getSingleProductError?.status === 500 ||
      (!isGetSingleProductLoading && !getSingleProductData && getSingleProductError))
  ) {
    return <NotFound404Page />
  }

  const isProductGenerated = getSingleProductData?.status !== 'draft'
  const retailersDataNames = retailersData?.map(retailer => retailer?.name)

  return (
    <>
      <div className="dark-bg-page ">
        <section className={clsx('light-header', styles.header, [isProductGenerated && styles.noUnderlined])}>
          <div className={navigationHeaderStyles.navigationHeader__container}>
            <WppBreadcrumb items={breadCrumbItems} middleTruncation onWppChange={handleRouteChange} />
            <Banner
              shouldBannerShow={!!toastDetails}
              text={toastDetails?.message}
              type={toastDetails?.type}
              onBannerClick={toastDetails?.onBannerClick}
              onClose={() => {
                if (!!toastDetails) setToastDetails(undefined)
              }}
            />
          </div>

          <PageHeader
            className={styles.titleContainer}
            isLoading={isInitialising}
            tags={
              isProductGenerated
                ? [statusChecker(getSingleProductData?.status).tag]
                : [renderStatus(getSingleProductData?.variants, getSingleProductData?.validation?.status)]
            }
            createdDetails={`${getSingleProductData?.created_at === getSingleProductData?.updated_at ? `Created: ${getFormattedDate(getSingleProductData?.created_at)}` : `Updated: ${getFormattedDate(getSingleProductData?.updated_at)}`} by ${getSingleProductData?.update_info?.user_name || unknown}`}
            pageTitle={getSingleProductData?.reference_name!}
            menuItems={menuItems}
            details={{
              Region: `${getSingleProductData?.region?.name} | ${getSingleProductData?.region?.locale.toUpperCase().replace('_', '-')}`,
              Retailers: retailersData.length < 1 ? 'No retailers selected' : retailersDataNames?.join(' | '),
            }}
          />

          {!isProductGenerated && (
            <Tabs
              currentTab={currentTab}
              dataKeys={[
                { displayText: 'Product data', view: 'product_data', icon: 'warning' },
                { displayText: 'Variant data', view: 'variant_data', icon: 'warning' },
                { displayText: 'Standard content', view: 'standard_content', icon: 'warning' },
              ]}
              showIconInTab={showIconInTab}
              align="left"
              onSetNewTab={value => {
                if (hasUnsavedChanges) {
                  setPendingNavigation(prev => {
                    if (!prev) {
                      return value as productTabsIds
                    } else return prev
                  })
                  setIsTryingToQuitWithChanges(true)
                  return
                }
                setCurrentTab(value as productTabsIds)
              }}
              isInitialising={false}
            />
          )}
        </section>

        <section className="page-content">
          {!isGetSingleProductLoading && getSingleProductData ? (
            <>
              {isProductGenerated ? (
                <RenderGeneratedProduct
                  productId={getSingleProductData.product_id}
                  projectRegionId={getSingleProductData.project_region_id}
                  setToastDetails={setToastDetails}
                  approveData={{
                    project_region_id: getSingleProductData.project_region_id,
                    product_ids: [getSingleProductData.product_id!],
                  }}
                />
              ) : (
                <RenderNotGeneratedProduct
                  activeTab={currentTab}
                  productAPI={productAPI}
                  setToastDetails={setToastDetails}
                  isProductDataFormActive={isProductDataFormActive}
                  setIsProductDataFormActive={setIsProductDataFormActive}
                  setShowIconInTab={setShowIconInTab}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              )}
            </>
          ) : (
            <LoadingSpinner />
          )}
        </section>
      </div>

      {isDeleteActive && (
        <GenericModal
          onSubmit={handleDeleteProduct}
          onCancel={() => setIsDeleteActive(false)}
          modalTitle="Delete product"
          submitButtonText="Delete"
          isApiLoading={isDeleteProductLoading}
          shouldModalOpen={isDeleteActive}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to delete this Product? This will remove it and all it's variants from your project.
          </WppTypography>
        </GenericModal>
      )}

      {hasUnsavedChanges && isTryingToQuitWithChanges && (
        <UnsavedChangesModal
          handleSubmit={function (): void {
            setHasUnsavedChanges(false)
            setIsTryingToQuitWithChanges(false)
            setPendingNavigation(undefined)

            if (pendingNavigation) {
              if (pendingNavigation.startsWith('/')) {
                navigate(pendingNavigation)
              } else {
                setCurrentTab(pendingNavigation as productTabsIds)
              }
            }
          }}
          onCancel={() => {
            setPendingNavigation(undefined)
            setIsTryingToQuitWithChanges(false)
          }}
          shouldModalOpen={hasUnsavedChanges && isTryingToQuitWithChanges}
        />
      )}

      {isBulkEditContentModalOpen && (
        <ManualUpload
          shouldModalOpen={isBulkEditContentModalOpen}
          onCancel={() => setIsBulkEditContentModalOpen(false)}
          onSuccessCb={() => {
            setToastDetails({
              message: 'Content uploaded successfully',
              type: 'success',
            })
            setIsBulkEditContentModalOpen(false)
          }}
        />
      )}
    </>
  )
}
